/**
 * 우측 드로어 "작업 상세" — 실행 상태 + thread 의 **살아있는 업무 산출물**.
 *
 * 구 `ArtifactsPanel` 이 상시 차지하던 오른쪽 칸을 접었다. 이유는 공간이 아니라 위계다:
 * 대화가 주(主)이고 산출물은 **필요할 때 여는 참조**다. 상시 노출은 좁은 화면에서 본문
 * 컬럼(46rem)을 먹고, 정작 비어 있을 때가 많아 "고장난 빈칸"으로 읽혔다.
 *
 * 진행 요약은 **pill·본문 카드와 같은 값**(`deriveSurface`)에서 나온다 — 표면마다 따로
 * 판단하면 셋이 어긋난다(DESIGN §5).
 *
 * 비어 있는 것은 오류가 아니다(백엔드도 404 대신 빈값을 준다) — 아직 아무것도 안 만든 대화가
 * 정상이므로, 빈 드로어가 "고장"으로 보이지 않게 문구로 말한다.
 */
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { request } from '@/api/client'
import { API } from '@/api/paths'
import { FileText, ShieldCheck, Wrench } from '@/ui/icons'
import { phaseKey } from '@/jobs/types'
import type { JobView } from '@/jobs/types'
import { drawerLineOf } from './surface'
import type { Surface } from './surface'

interface ThreadArtifacts {
  intake?: { fields?: { name: string; value?: string | null; status?: string }[] } | null
  sourcing?: unknown
  assignment?: unknown
  wbs?: unknown
  outputs?: { destination?: string; title?: string; status?: string }[]
  orders?: { vendor?: string; item?: string; amount?: number }[]
}

export function WorkDrawer({
  threadId,
  job,
  surface,
}: {
  threadId: string | null
  job: JobView | null
  surface: Surface
}) {
  const { t } = useTranslation()
  const { data, isLoading } = useQuery<ThreadArtifacts>({
    queryKey: ['thread-artifacts', threadId],
    queryFn: () => request<ThreadArtifacts>(API.artifacts(threadId as string)),
    enabled: Boolean(threadId),
    staleTime: 5_000,
  })

  const line = drawerLineOf(surface)
  const outputs = data?.outputs ?? []
  const orders = data?.orders ?? []
  const filled = (data?.intake?.fields ?? []).filter((field) => field.value)
  const empty = !isLoading && filled.length === 0 && outputs.length === 0 && orders.length === 0

  return (
    <>
      <div className="d-sec">
        <h3>{t('drawer.progress')}</h3>
        {/* 고정 단계 목록을 그리지 않는다(UI-D5(a)) — 없는 이벤트를 단계로 지어내지 않는다. */}
        <div className="dp-simple">
          <div className={`dp-line ${line.tone}`}>
            <strong>{t(line.key)}</strong>
          </div>
          {job && surface.kind === 'job' && (
            <div className="dp-line">
              <span className="dp-tool">
                <Wrench size={12} /> {t('job.phase_now')} · <code>{t(phaseKey(job.phase))}</code>
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="d-sec">
        <h3>
          {t('artifacts.title')}
          <span className="d-count">{filled.length + outputs.length + orders.length}</span>
        </h3>

        {isLoading && <p className="d-empty">{t('common.loading')}</p>}
        {empty && <p className="d-empty">{t('artifacts.empty')}</p>}

        {filled.length > 0 && (
          <dl className="d-fields">
            {filled.slice(0, 12).map((field) => (
              <div key={field.name} className="contents">
                <dt>{field.name}</dt>
                <dd>{field.value}</dd>
              </div>
            ))}
          </dl>
        )}

        {outputs.length > 0 && (
          <>
            <h3 style={{ marginTop: filled.length ? 16 : 0 }}>{t('artifacts.outputs')}</h3>
            {outputs.map((output, index) => (
              <div className="src-row" key={`o${index}`}>
                <FileText size={15} />
                <span className="src-meta">
                  <span className="src-label">{output.title ?? t('artifacts.draft')}</span>
                  <span className="src-kind">{output.destination}</span>
                </span>
              </div>
            ))}
          </>
        )}

        {orders.length > 0 && (
          <>
            {/* 발주는 유일한 비가역 기록이라 눈에 띄게 둔다. */}
            <h3 style={{ marginTop: 16 }}>{t('artifacts.orders')}</h3>
            {orders.map((order, index) => (
              <div className="src-row" key={`r${index}`}>
                <FileText size={15} />
                <span className="src-meta">
                  <span className="src-label">{order.vendor}</span>
                  <span className="src-kind">{order.item}</span>
                </span>
                <span className="chip dangerc">{t('artifacts.irreversible')}</span>
              </div>
            ))}
          </>
        )}
      </div>

      <div className="audit-note">
        <ShieldCheck size={15} />
        <div>
          <strong>{t('drawer.audit_title')}</strong>
          <span>{t('drawer.audit_body')}</span>
        </div>
      </div>
    </>
  )
}
